export type BillingStatus =
    | 'PENDING'
    | 'PAID'
    | 'OVERDUE'
    | 'CANCELLED'
    | 'REFUNDED'
    | 'PARTIALLY_REFUNDED'
    | 'PARTIALLY_PAID'
    | 'IN_DISPUTE'
    | 'WRITTEN_OFF'

export type BillingPaymentMethod =
    | 'PIX'
    | 'BOLETO'
    | 'CREDIT_CARD'
    | 'DEBIT_CARD'
    | 'CASH'
    | 'BANK_TRANSFER'
    | 'CHECK'
    | 'TERMINAL'
    | 'OTHER'

/**
 * Métodos em que o dinheiro **não passa pela conta digital** — espelha
 * `OFFLINE_PAYMENT_METHODS` do backend (`libs/common/billing-settlement`).
 *
 * Whitelist fechada de propósito: transferência bancária e cheque ficam de fora,
 * porque têm comprovante de terceiro que vale conferir. Usado para decidir quem pode
 * aprovar um estorno — sem saldo do provedor envolvido, não depende do operador eleito.
 */
export const OFFLINE_PAYMENT_METHODS: BillingPaymentMethod[] = [
    'CASH',
    'TERMINAL',
    'OTHER',
]

export function isOfflinePaymentMethod(method?: string | null): boolean {
    return !!method && OFFLINE_PAYMENT_METHODS.includes(method as BillingPaymentMethod)
}

export type BillingType = 'RECEIVABLE' | 'PAYABLE'

/** Pagador da cobrança: o paciente (padrão) ou o convênio que cobre o atendimento. */
export type BillingPayerType = 'CLIENT' | 'HEALTH_PLAN'

/** Como o convênio é faturado. TISS_BATCH ainda não é suportado. */
export type HealthPlanSettlementMode = 'DIRECT_INVOICE' | 'TISS_BATCH'

export type FeeMode = 'FIXED' | 'PERCENT'

/** Linha de detalhamento de uma taxa aplicada à cobrança (Billing.fees). */
export interface FeeLine {
    label: string
    method: string
    mode: FeeMode
    rate: number
    baseAmount: number
    feeAmount: number
}

/** Agendamento/pedido que originou a cobrança (resolvido no findOne do backend). */
export interface BillingOrigin {
    type: 'CONSULTATION' | 'EXAM' | 'PROCEDURE' | 'TELECONSULTATION' | 'ORDER'
    id: number
    title?: string | null
    scheduledAt?: string | null
    status?: string | null
}

/** Cobrança vizinha (original ou complemento) referenciada por outra. */
export interface BillingLink {
    id: number
    amount: number
    status: BillingStatus
    description: string | null
    dueDate: string
    paidAt: string | null
    installmentNumber?: number | null
    installmentTotal?: number | null
    installmentGroupId?: string | null
}

export interface Billing {
    id: number
    origin?: BillingOrigin | null
    /**
     * TODOS os atendimentos cobertos por esta cobrança. Com o rateio de repasse,
     * uma cobrança pode cobrir vários (dois exames e uma consulta na mesma visita);
     * `origin` é o primeiro deles, mantido para o código que já o usa.
     */
    origins?: BillingOrigin[] | null
    /**
     * Solicitação que mantém a cobrança em `PENDING_APPROVAL` (baixa, cancelamento,
     * estorno ou quitação). `null` numa conta a pagar aguardando o operador: ela
     * própria é o item da fila.
     */
    pendingApprovalRequest?: {
        id: number
        action: 'MARK_PAID' | 'CANCEL' | 'REFUND' | 'SETTLE_PROCEDURE'
        createdAt: string
        requestedBy?: { id: number; name: string | null } | null
    } | null
    type: BillingType
    clientId: number | null
    client?: {
        id: number
        name: string
        email: string | null
        whatsAppNumber: string | null
    }
    /**
     * Quem PAGA. Em `HEALTH_PLAN`, o paciente em `client` continua sendo o dono do
     * atendimento, mas quem deve é o convênio — e a cobrança não tem link de
     * pagamento nem entra na régua de cobrança do paciente.
     */
    payerType?: BillingPayerType
    /**
     * Quanto o convênio deve por este atendimento. Presente só quando
     * `payerType = HEALTH_PLAN` — e nesse caso `amount` vale ZERO de propósito
     * (quem cobra o convênio é a fatura que agrupa o período). Onde a tela mostra
     * o valor do atendimento, é ESTE campo que vale; usar `amount` exibe R$ 0,00 e
     * produz margem negativa contra o repasse ao parceiro.
     */
    invoicedAmount?: number | null
    healthPlanId?: number | null
    healthPlan?: {
        id: number
        name: string
        ansCode: string | null
        cnpj: string | null
        settlementMode: HealthPlanSettlementMode
    } | null
    companyId: number
    company?: {
        id: number
        name: string
        tradingName: string | null
    }
    partnerId: number | null
    partner?: {
        id: number
        name: string
        tradingName: string | null
    } | null
    professionalId: number | null
    professional?: {
        id: number
        name: string
        email: string | null
    } | null
    createdById: number | null
    createdBy?: {
        id: number
        name: string
    } | null

    supplierId: number | null
    supplier?: {
        id: number
        name: string
        tradingName: string | null
        cnpj: string | null
    } | null
    supplierName: string | null
    hospitalId: number | null
    hospital?: {
        id: number
        name: string
        tradingName: string | null
        cnpj: string | null
    } | null
    category: string | null
    financialCategoryId: number | null
    costCenterId: number | null
    bankAccountId: number | null

    description: string | null
    notes: string | null
    amount: number
    paidAmount: number | null
    discount: number | null
    interest: number | null
    installmentNumber: number | null
    installmentTotal: number | null
    referenceCode: string | null

    /**
     * Repasse: os VALORES moram nas fatias (`payoutShares`), uma por atendimento —
     * uma cobrança pode cobrir vários. Aqui ficam só os destinatários, que seguem
     * sendo colunas do Billing. Ver docs/billing-payout-share.md.
     */
    payoutShares?: Array<{
        id: number
        sourceType: string
        sourceId: number
        amount: number
        partnerId: number | null
        professionalId: number | null
        hospitalId: number | null
        partnerAmount: number | null
        professionalAmount: number | null
        hospitalAmount: number | null
        suppliesAmount: number | null
    }> | null

    // Taxas de cobrança: amount já inclui feeAmount; fees traz o detalhamento.
    feeAmount: number | null
    fees: FeeLine[] | null

    status: BillingStatus
    paymentMethod: BillingPaymentMethod | null
    /**
     * Parcelas no cartão combinadas com o pagador — trava o checkout. Não confundir
     * com `installmentTotal`, que é o parcelamento próprio (uma cobrança por parcela).
     */
    installmentCount: number | null
    dueDate: string
    paidAt: string | null
    cancelledAt: string | null

    // Estorno. `refundedAmount` é o ACUMULADO devolvido — comparado com paidAmount,
    // é o que distingue devolução parcial de integral. `refundedVia` só existe no
    // estorno feito por fora da conta digital (registra a instituição).
    refundedAmount: number | null
    refundedAt: string | null
    refundedVia: string | null
    refundReason: string | null

    paymentLink: string | null

    pixKey: string | null
    pixQrCode: string | null
    pixQrCodeBase64: string | null
    pixCopyPaste: string | null
    pixExpirationDate: string | null

    bankSlipUrl: string | null
    nossoNumero: string | null
    barcode: string | null
    digitableLine: string | null

    provider: string | null
    providerPaymentId: string | null
    providerOrderId: string | null
    publicToken: string | null

    // Ajuste de valor de atendimento pago: a diferença vira uma cobrança COMPLEMENTAR,
    // ligada à original. `parentBilling` é a original (quando esta é o complemento);
    // `adjustments` são os complementos gerados a partir desta.
    parentBillingId: number | null
    parentBilling?: BillingLink | null
    adjustments?: BillingLink[] | null

    metadata: any
    attachments: BillingAttachment[] | null

    isReconciled: boolean
    reconciliationPending: boolean
    reconciledAt: string | null
    reconciledById: number | null
    reconciledBy?: {
        id: number
        name: string
    } | null
    reconciliationNotes: string | null

    /** Aprovação (contas a pagar). */
    approvalStatus?:
        | 'NOT_REQUIRED'
        | 'PENDING_APPROVAL'
        | 'APPROVED'
        | 'REJECTED'
        | null
    approvedAt?: string | null
    approvedBy?: { id: number; name: string } | null
    rejectedReason?: string | null

    /** Transferência disparada na aprovação (conta digital / BaaS). */
    transfer?: BillingTransfer | null

    /** Conta a pagar quitada por fora da conta digital (outro banco/instituição). */
    externalTransferVia?: string | null
    externalTransferAt?: string | null
    externalTransferBy?: { id: number; name: string } | null

    createdAt: string
    updatedAt: string
}

/** Rastro do dinheiro saindo pela conta digital. */
export interface BillingTransfer {
    id: number
    status:
        | 'PENDING_APPROVAL'
        | 'APPROVED'
        | 'REJECTED'
        | 'PROCESSING'
        | 'DONE'
        | 'FAILED'
        | 'CANCELLED'
    amount: number
    pixKey?: string | null
    pixKeyType?: string | null
    bankCode?: string | null
    bankAgency?: string | null
    bankAccountNumber?: string | null
    providerTransferId?: string | null
    externalReference?: string | null
    executedAt?: string | null
    failReason?: string | null
    createdAt: string
}

/** O que a IA leu do comprovante (gravado no próprio anexo pelo cron de leitura). */
export interface BillingReceiptReading {
    readable: boolean
    reviewedAt: string
    valueMatches?: boolean
    trustworthy?: boolean
    findings?: string[]
    data?: {
        valor?: number | null
        data_pagamento?: string | null
        beneficiario?: string | null
        pagador?: string | null
        tipo?: string | null
        codigo_transacao?: string | null
        banco?: string | null
        confianca?: number | null
    }
}

export interface BillingAttachment {
    url: string
    name: string
    description?: string
    /** Laudo da leitura do comprovante pela IA. */
    reading?: BillingReceiptReading
    /** sha256 do conteúdo — identifica o documento entre uploads. */
    hash?: string
    /**
     * Recusado por um operador (rejeição com categoria "Comprovante"). Permanece
     * na cobrança para auditoria, mas não vale como prova de pagamento nem pode
     * ser reenviado.
     */
    rejected?: boolean
    rejectedAt?: string
    rejectedReason?: string
}

export interface CreateBillingData {
    type?: BillingType
    clientId?: number
    /** HEALTH_PLAN exige healthPlanId e não gera link de pagamento ao paciente. */
    payerType?: BillingPayerType
    healthPlanId?: number | null
    companyId?: number
    partnerId?: number
    professionalId?: number
    supplierId?: number
    supplierName?: string
    hospitalId?: number | null
    category?: string
    financialCategoryId?: number
    costCenterId?: number
    bankAccountId?: number
    description?: string
    notes?: string
    amount: number
    paidAmount?: number
    discount?: number
    interest?: number
    installmentNumber?: number
    installmentTotal?: number
    referenceCode?: string
    paymentMethod?: BillingPaymentMethod
    /**
     * Pago em (yyyy-MM-dd): dia que a baixa automática do recebido por fora (dinheiro,
     * maquininha, "outro") registra. Não marca a cobrança como paga sozinho.
     */
    paidAt?: string
    dueDate: string
    paymentLink?: string
    pixKey?: string
    pixQrCode?: string
    pixQrCodeBase64?: string
    pixCopyPaste?: string
    pixExpirationDate?: string
    bankSlipUrl?: string
    nossoNumero?: string
    barcode?: string
    digitableLine?: string
    provider?: string
    providerPaymentId?: string
    providerOrderId?: string
    metadata?: any
    attachments?: BillingAttachment[]
    enableRecurring?: boolean
    recurringDueDay?: number
}

export interface UpdateBillingData extends Partial<CreateBillingData> {}
